/**
 * Section navigation of the Settings page (W2-13 settings-keymap; DESIGN.md §4.20): one link per section with its
 * icon, the section in view marked as current (aria-current), and while searching the number of matches per
 * section (sections without a match are dimmed and skipped). Arrow keys move between links (Home / End jump).
 */

import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Icon } from '../../ui';
import type { SettingsText } from './hooks';
import type { PageSection, PageSectionId } from './settingsCatalog';
import { m } from './settings.messages';
import s from './Settings.module.css';

export interface SettingsNavProps {
  readonly sections: readonly PageSection[];
  readonly current: PageSectionId;
  /** Matches per section while searching (null when not searching). */
  readonly counts: ReadonlyMap<PageSectionId, number> | null;
  readonly text: SettingsText;
  onSelect(id: PageSectionId): void;
}

export function SettingsNav(props: SettingsNavProps) {
  const { t } = props.text;
  const links = useRef(new Map<PageSectionId, HTMLAnchorElement>());

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>): void => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const order = props.sections.map((sec) => sec.id);
    const from = order.findIndex((id) => links.current.get(id) === e.target);
    if (from < 0) return;
    let to = -1;
    if (e.key === 'ArrowDown') to = (from + 1) % order.length;
    else if (e.key === 'ArrowUp') to = (from - 1 + order.length) % order.length;
    else if (e.key === 'Home') to = 0;
    else if (e.key === 'End') to = order.length - 1;
    if (to < 0) return;
    e.preventDefault();
    const id = order[to];
    if (id !== undefined) links.current.get(id)?.focus();
  };

  return (
    <nav className={s.nav} aria-label={t(m.navLabel)} onKeyDown={onKeyDown}>
      <ul className={s.navList}>
        {props.sections.map((sec) => {
          const count = props.counts === null ? null : (props.counts.get(sec.id) ?? 0);
          const empty = count === 0;
          const title = t(sec.title);
          return (
            <li key={sec.id}>
              <a
                ref={(el) => {
                  if (el === null) links.current.delete(sec.id);
                  else links.current.set(sec.id, el);
                }}
                href={`#/settings/${sec.id}`}
                className={s.navLink}
                aria-current={props.current === sec.id ? 'location' : undefined}
                data-empty={empty ? 'true' : undefined}
                aria-label={count === null ? undefined : t(m.navMatches, { title, count })}
                onClick={(e) => {
                  e.preventDefault();
                  props.onSelect(sec.id);
                }}
              >
                <Icon name={sec.icon} size={16} />
                <span className={s.navTitle}>{title}</span>
                {count !== null && (
                  <span className={s.navCount} aria-hidden>
                    {count}
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
